import "./explorerDataGrid.scss";
import {
  DataGridPremium,
  GridColDef,
  gridClasses,
  GridColTypeDef,
  useGridApiRef,
  GridRowModel,
  GridActionsCellItem,
  gridStringOrNumberComparator,
  gridExpandedSortedRowIdsSelector,
  GridSortModel,
  GridFilterModel,
  GridPinnedColumnFields,
  GridColumnMenu,
  GridColumnMenuProps,
  GridPreferencePanelsValue,
} from "@mui/x-data-grid-premium";
import dayjs from "dayjs";
import { TableData } from "../../../models/TableData";
import { useMemo, useEffect, useState, useRef } from "react";
import FilterContextCell, {
  getFilterContextMetricType,
} from "./FilterContextCell";
import {
  Box,
  ClickAwayListener,
  IconButton,
  InputBase,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popper,
  Tooltip,
} from "@mui/material";
import {
  Add,
  ArrowDownward,
  ArrowUpward,
  Check,
  Close,
  Edit,
} from "@mui/icons-material";
import CustomDataGridToolbar from "./CustomDataGridToolbar";
import PercentileCell from "../PercentileCell";
import { ExplorerEnvironment } from "../../../data/Cache";
import { useGlossary } from "../../../hooks/useGlossary";
import { grainLabel } from "../../../models/Glossary";
import type { ValueLookup } from "./VideoCellHandler";
import type { ExplorerQueryContext } from "../../../models/Explorer";
import {
  decodeTableState,
  isDefaultState,
  buildUrlWithTableState,
  TableStateSnapshot,
  PinMatcher,
  resolvePinMatchers,
  rowMatchesPinMatcher,
  buildPinMatcher,
  serializeCustomColumn,
  deserializeCustomColumn,
} from "../../../util/tableState";
import {
  CustomColumn,
  collectColumnRefs,
  formatCustomValue,
  materializeCustomColumns,
} from "../../../util/customColumns";
import CustomColumnsPanel from "./CustomColumnsPanel";

// Header rename editor rendered as a Popper anchored to the column header
// element. We don't render inside the column header's title slot because
// that title is sized by column width — which can be as narrow as ~60px,
// leaving no room for the input. The popper has its own dimensions and
// floats above the grid.
function HeaderRenamePopper({
  field,
  anchorEl,
  initial,
  placeholder,
  onSave,
  onCancel,
}: {
  field: string;
  anchorEl: HTMLElement | null;
  initial: string;
  placeholder: string;
  onSave: (name: string) => void;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  const inputRef = useRef<HTMLInputElement>(null);

  // Re-seed the input when the user switches which column is being renamed
  // without ever closing the popper (rare but possible).
  useEffect(() => setValue(initial), [field, initial]);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  if (!anchorEl) return null;

  return (
    <Popper
      open
      anchorEl={anchorEl}
      placement="bottom-start"
      modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
      sx={{ zIndex: 1300 }}
    >
      <ClickAwayListener onClickAway={onCancel}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            backgroundColor: "#fff",
            border: "1px solid #4c4c4c",
            borderRadius: "3px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
            p: 0.75,
            minWidth: 240,
          }}
        >
          <InputBase
            inputRef={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                onSave(value);
              } else if (e.key === "Escape") {
                e.preventDefault();
                onCancel();
              }
            }}
            placeholder={placeholder}
            fullWidth
            sx={{
              flex: 1,
              fontSize: "0.85rem",
              "& input": {
                padding: "4px 8px",
                border: "1px solid #bdbdbd",
                borderRadius: "2px",
                "&:focus": { borderColor: "#4c4c4c" },
              },
            }}
          />
          <Tooltip title="Save (Enter)">
            <IconButton
              size="small"
              onMouseDown={(e) => {
                e.preventDefault();
                onSave(value);
              }}
              sx={{ p: 0.5, color: "#2e7d32" }}
            >
              <Check fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel (Esc)">
            <IconButton
              size="small"
              onMouseDown={(e) => {
                e.preventDefault();
                onCancel();
              }}
              sx={{ p: 0.5, color: "#c62828" }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </ClickAwayListener>
    </Popper>
  );
}

// Column menu override — adds "Rename column" above the standard items.
// Suppressed for synthetic columns (Rank, actions) which have no user-facing name.
const RENAME_HIDDEN_FIELDS = new Set(["Rank", "actions"]);

const VALID_OPERATORS_BY_TYPE: Record<string, Set<string>> = {
  string: new Set([
    "contains", "doesNotContain", "equals", "doesNotEqual",
    "startsWith", "endsWith", "isEmpty", "isNotEmpty", "isAnyOf",
  ]),
  number: new Set([
    "=", "!=", ">", ">=", "<", "<=", "isEmpty", "isNotEmpty", "isAnyOf",
  ]),
  boolean: new Set(["is"]),
  date: new Set([
    "is", "not", "after", "onOrAfter", "before", "onOrBefore",
    "isEmpty", "isNotEmpty",
  ]),
  dateTime: new Set([
    "is", "not", "after", "onOrAfter", "before", "onOrBefore",
    "isEmpty", "isNotEmpty",
  ]),
};

function RenameMenuItem({
  field,
  onRequestRename,
  hideMenu,
}: {
  field: string;
  onRequestRename: (field: string) => void;
  hideMenu?: () => void;
}) {
  return (
    <MenuItem
      onClick={() => {
        hideMenu?.();
        onRequestRename(field);
      }}
    >
      <ListItemIcon>
        <Edit fontSize="small" />
      </ListItemIcon>
      <ListItemText>Rename column</ListItemText>
    </MenuItem>
  );
}

function AddCustomColumnMenuItem({
  onRequestNewCustomColumn,
  hideMenu,
}: {
  onRequestNewCustomColumn?: () => void;
  hideMenu?: () => void;
}) {
  return (
    <MenuItem
      onClick={() => {
        hideMenu?.();
        onRequestNewCustomColumn?.();
      }}
    >
      <ListItemIcon>
        <Add fontSize="small" />
      </ListItemIcon>
      <ListItemText>Add custom column…</ListItemText>
    </MenuItem>
  );
}

function CustomColumnMenu(
  props: GridColumnMenuProps & {
    onRequestRename?: (field: string) => void;
    onRequestNewCustomColumn?: () => void;
  },
) {
  const { onRequestRename, onRequestNewCustomColumn, ...rest } = props;
  const field = rest.colDef?.field;
  const skipRename = !field || RENAME_HIDDEN_FIELDS.has(field);

  return (
    <GridColumnMenu
      {...rest}
      slots={{
        ...(skipRename ? {} : { columnMenuRenameItem: RenameMenuItem }),
        columnMenuAddCustomItem: AddCustomColumnMenuItem,
      }}
      slotProps={{
        columnMenuRenameItem: {
          displayOrder: 5,
          field,
          onRequestRename,
        } as any,
        columnMenuAddCustomItem: {
          displayOrder: 6,
          onRequestNewCustomColumn,
        } as any,
      }}
    />
  );
}

const DEFAULT_SLOTS = {
  toolbar: CustomDataGridToolbar,
  columnsPanel: CustomColumnsPanel,
  columnMenu: CustomColumnMenu,
};

// Embedded mode (Phase 3 research wiki) wants a chromeless grid: no
// toolbar (no Columns / Filters / Percentiles / Export / Share), no
// 3-dot column menu, and no per-row pin actions. We achieve this by
// (a) leaving the toolbar slot unset, (b) passing `disableColumnMenu`
// to MUI, and (c) skipping the actions column in finalColumns. The
// `columnsPanel` slot is only ever rendered inside the toolbar's
// Columns button, so it doesn't need a noop here.
const EMBEDDED_SLOTS = {};

export default function ExplorerDataGrid({
  data,
  loading,
  isMobile = false,
  queryContext,
  environment = ExplorerEnvironment.NBA,
  valueLookup,
  initialTableState,
  filterNames = [],
  embedded = false,
}: {
  data: TableData;
  loading: boolean;
  isMobile?: boolean;
  /** exec_query's queryContext: lazy cells merge its filters and forward its source */
  queryContext?: ExplorerQueryContext;
  environment?: ExplorerEnvironment;
  valueLookup?: ValueLookup;
  initialTableState?: string;
  filterNames?: string[];
  /** Renders the grid in embedded mode used by the research wiki:
   *  drops the toolbar, the row-pin actions column, and the per-column
   *  3-dot menus, and switches to the dark-header look used elsewhere
   *  in the team-report aesthetic. Default `false` — existing callers
   *  see no behavior change. */
  embedded?: boolean;
}) {
  const apiRef = useGridApiRef();
  // Pin matchers: content-based row identity using dimension column values.
  // Each matcher is a partial key-value map — a row is pinned if ALL keys match.
  const [pinnedRowMatchers, setPinnedRowMatchers] = useState<{
    top: PinMatcher[];
    bottom: PinMatcher[];
  }>({ top: [], bottom: [] });
  const [rankMode, setRankMode] = useState<"ranks" | "percentiles" | "off">(
    "percentiles",
  );
  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [quickFilterValues, setQuickFilterValues] = useState<string>("");
  // Track user's custom column order to persist across column rebuilds
  const [orderedFields, setOrderedFields] = useState<string[]>([]);
  // Track user's custom column widths to persist across column rebuilds
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  // User-defined computed columns; values are materialized into row data
  const [customColumns, setCustomColumns] = useState<CustomColumn[]>([]);
  // Per-field display name overrides (rename headers without changing field IDs)
  const [displayNames, setDisplayNames] = useState<Record<string, string>>({});
  // When set, the rename popper opens anchored to that column's header.
  // Triggered from the column menu's "Rename column" item.
  const [renamingField, setRenamingField] = useState<string | null>(null);
  const [renamingAnchor, setRenamingAnchor] = useState<HTMLElement | null>(
    null,
  );
  // Locate a column's header element via the grid's root, so the rename
  // popper can anchor to it. data-field + role=columnheader is stable across
  // MUI X v5–v7.
  const findHeaderEl = (field: string): HTMLElement | null => {
    const root = apiRef.current?.rootElementRef?.current as
      | HTMLElement
      | undefined;
    if (!root) return null;
    return root.querySelector<HTMLElement>(
      `[data-field="${field}"][role="columnheader"]`,
    );
  };
  const requestRename = (field: string) => {
    apiRef.current?.hideColumnMenu?.();
    setRenamingField(field);
    setRenamingAnchor(findHeaderEl(field));
  };
  // Imperative bridge for the column menu's "Add custom column…" item.
  // The panel registers an "open editor" callback here when it mounts; we
  // invoke it after showPreferences to expand the editor. We use a ref +
  // setTimeout instead of derived state because: (a) the panel mounts after
  // showPreferences fires, so a state-based signal would race against mount,
  // and (b) ref-based imperative calls don't need to track "rising edges"
  // through React's render cycle, which proved fragile in practice.
  const openCustomColumnEditorRef = useRef<(() => void) | null>(null);
  const requestNewCustomColumn = () => {
    apiRef.current?.hideColumnMenu?.();
    apiRef.current?.showPreferences?.(GridPreferencePanelsValue.columns);
    // Defer to the next tick so the panel has mounted and registered.
    setTimeout(() => openCustomColumnEditorRef.current?.(), 0);
  };
  // Controlled pinned columns state — allows both our defaults and user pin actions from column menu.
  // Embedded mode drops the actions column entirely, so its right-pin slot is empty.
  const [pinnedColumns, setPinnedColumns] = useState<GridPinnedColumnFields>(
    isMobile
      ? { left: ["Rank"] }
      : embedded
        ? { left: ["Rank"] }
        : { left: ["Rank"], right: ["actions"] },
  );
  // Column visibility — hide Rank on mobile by default, user can still toggle via column menu
  const [columnVisibilityModel, setColumnVisibilityModel] = useState<
    Record<string, boolean>
  >(isMobile ? { Rank: false } : {});
  const percentileProps = { rankMode, setRankMode };
  // Track whether URL table state has been applied (once per initialTableState value).
  // Applied in two phases:
  //   - phase 1: customColumns are set (so the grid can register their column
  //     defs before sort/visibility/order reference them)
  //   - phase 2: everything else (sort, visibility, order, etc.)
  // This prevents MUI from silently dropping a sortModel directive that
  // points at a custom column not yet present in the columns prop.
  const appliedUrlState = useRef(false);
  const customColumnsPhaseApplied = useRef(false);
  // Number of CCs Phase 1 committed via setCustomColumns. Phase 2 waits for
  // customColumns.length to reach this count so MUI sees the new column
  // defs before sortModel/visibility/etc. reference them. -1 = Phase 1 hasn't run.
  const phase1CommittedCcCount = useRef(-1);
  const lastAppliedTableState = useRef<string | undefined>(undefined);

  // Reset the applied flags when initialTableState changes (e.g., loading a different saved query)
  if (initialTableState !== lastAppliedTableState.current) {
    if (initialTableState !== undefined) {
      appliedUrlState.current = false;
      customColumnsPhaseApplied.current = false;
      phase1CommittedCcCount.current = -1;
    }
    lastAppliedTableState.current = initialTableState;
  }

  // Dimension columns in the current data — used to build pin matchers on user click.
  // Matches raw filter names AND dependent/split columns like "FilterA_as_FilterB".
  const dimensionColumns = useMemo(() => {
    if (!data || data.length === 0 || filterNames.length === 0) return [];
    const filterSet = new Set(filterNames);
    const dataCols = Object.keys(data[0]);
    const dims = dataCols.filter((col) => {
      // Direct match: column name is a known filter
      if (filterSet.has(col)) return true;
      // Dependent filter: "FilterA_as_FilterB[, FilterC]"
      const asIdx = col.indexOf("_as_");
      if (asIdx > 0) {
        const base = col.substring(0, asIdx);
        return filterSet.has(base);
      }
      return false;
    });
    return dims;
  }, [data, filterNames]);

  // Detect "Overall" rows and denormalize their metric values as hidden columns
  // on every row in the same primary dimension group. This enables filtering
  // entire groups by overall values (e.g., "Overall Poss >= 200").
  // Detect primary/secondary dimension grouping and denormalize every secondary
  // dimension value's metrics onto every row in the same primary group.
  // This enables cross-row filtering like "Shot: % of Poss >= 50" on Overall rows.
  const { enrichedData: enrichedDataBase, hiddenColumns } = useMemo(() => {
    if (!data || data.length === 0 || dimensionColumns.length === 0) {
      return { enrichedData: data, hiddenColumns: [] as string[] };
    }

    // Find secondary dimension columns (those containing "Overall" values)
    const secondaryDims = dimensionColumns.filter((col) =>
      data.some((row) => row[col] === "Overall"),
    );
    if (secondaryDims.length === 0) {
      return { enrichedData: data, hiddenColumns: [] as string[] };
    }

    // Primary dimensions = dimension columns without "Overall" values
    const primaryDims = dimensionColumns.filter(
      (col) => !secondaryDims.includes(col),
    );
    if (primaryDims.length === 0) {
      return { enrichedData: data, hiddenColumns: [] as string[] };
    }

    // Metric columns = all non-dimension columns
    const dimSet = new Set(dimensionColumns);
    const metricCols = Object.keys(data[0]).filter((col) => !dimSet.has(col));

    // Build a lookup: primary key → { secondaryValue → metrics }
    // This captures every secondary dimension value's metrics per group
    const groupLookup = new Map<string, Map<string, Record<string, any>>>();
    for (const row of data) {
      const primaryKey = primaryDims
        .map((d) => String(row[d] ?? ""))
        .join("\0");
      // Composite secondary key for multi-secondary-dim cases
      const secondaryKey = secondaryDims
        .map((d) => String(row[d] ?? ""))
        .join(", ");

      if (!groupLookup.has(primaryKey)) {
        groupLookup.set(primaryKey, new Map());
      }
      const metrics: Record<string, any> = {};
      for (const col of metricCols) {
        metrics[col] = row[col];
      }
      groupLookup.get(primaryKey)!.set(secondaryKey, metrics);
    }

    // Collect all unique secondary values across all groups
    const allSecondaryValues = new Set<string>();
    Array.from(groupLookup.values()).forEach((group) => {
      Array.from(group.keys()).forEach((secKey) => {
        allSecondaryValues.add(secKey);
      });
    });

    // Build the new column names: "{SecondaryValue}: {Metric}"
    const newCols: string[] = [];
    Array.from(allSecondaryValues).forEach((secVal) => {
      metricCols.forEach((metric) => {
        newCols.push(`${secVal}: ${metric}`);
      });
    });

    // Enrich each row with all secondary values' metrics from its group
    const enriched = data.map((row) => {
      const primaryKey = primaryDims
        .map((d) => String(row[d] ?? ""))
        .join("\0");
      const group = groupLookup.get(primaryKey);
      if (!group) return row;

      const newRow = { ...row };
      Array.from(group.entries()).forEach(([secVal, metrics]) => {
        metricCols.forEach((col) => {
          newRow[`${secVal}: ${col}`] = metrics[col];
        });
      });
      return newRow;
    });

    return { enrichedData: enriched, hiddenColumns: newCols };
  }, [data, dimensionColumns]);

  // Materialize custom columns into the row data. Kept in a separate memo so
  // formula edits don't invalidate the (more expensive) primary→secondary
  // denormalization above. Downstream consumers see custom columns as
  // ordinary string-valued fields, identical to backend-returned columns.
  const enrichedData = useMemo(
    () => materializeCustomColumns(enrichedDataBase, customColumns),
    [enrichedDataBase, customColumns],
  );

  // Resolve pin matchers to numeric row IDs against current data
  const resolvedPinnedIds = useMemo(() => {
    if (!data || data.length === 0)
      return { top: [] as number[], bottom: [] as number[] };
    return {
      top: resolvePinMatchers(pinnedRowMatchers.top, data),
      bottom: resolvePinMatchers(pinnedRowMatchers.bottom, data),
    };
  }, [data, pinnedRowMatchers]);

  // Normalize pin matchers when data or dimensions change:
  // 1. Prune keys for dimensions no longer in the data (prevents orphaned conditions)
  // 2. Expand partial matchers into one fully-specified matcher per matching row
  //    (so each pinned row can be unpinned independently)
  useEffect(() => {
    if (!data || data.length === 0 || dimensionColumns.length === 0) return;

    setPinnedRowMatchers((prev) => {
      const dimSet = new Set(dimensionColumns);

      const normalize = (matchers: PinMatcher[]): PinMatcher[] => {
        if (matchers.length === 0) return matchers;

        const newMatchers: PinMatcher[] = [];
        const seen = new Set<string>();

        for (const matcher of matchers) {
          // Prune keys not in current dimension columns
          const pruned: PinMatcher = {};
          for (const [key, val] of Object.entries(matcher)) {
            if (dimSet.has(key)) pruned[key] = val;
          }
          // Drop empty matchers (all referenced dimensions were removed)
          if (Object.keys(pruned).length === 0) continue;

          // Expand: find all matching rows, build one fully-specified matcher each
          for (let i = 0; i < data.length; i++) {
            if (!rowMatchesPinMatcher(pruned, data[i])) continue;
            const full = buildPinMatcher(data[i], dimensionColumns);
            const key = JSON.stringify(full);
            if (!seen.has(key)) {
              seen.add(key);
              newMatchers.push(full);
            }
          }
        }

        return newMatchers;
      };

      const newTop = normalize(prev.top);
      const newBottom = normalize(prev.bottom);

      // Bail out if nothing changed (prevents infinite re-render)
      if (
        JSON.stringify(newTop) === JSON.stringify(prev.top) &&
        JSON.stringify(newBottom) === JSON.stringify(prev.bottom)
      ) {
        return prev;
      }

      return { top: newTop, bottom: newBottom };
    });
  }, [data, dimensionColumns]);

  // Effect to handle initial sort when data arrives
  useEffect(() => {
    if (data && data.length > 0) {
      // Skip default sort if URL table state will be applied instead
      if (initialTableState && !appliedUrlState.current) return;
      const api = apiRef.current;
      if (api) {
        const currentSortModel = api.getSortModel();
        if (currentSortModel.length === 0) {
          api.setSortModel([{ field: "Poss", sort: "desc" }]);
        }
      }
    }
  }, [data, apiRef, initialTableState]);

  // When data changes, prune state entries whose target fields no longer
  // exist. Skip on first mount until the URL-read effect has applied — that
  // way URL-restored state isn't wiped before it lands.
  //
  // We PRUNE rather than RESET so user adjustments (column order, widths,
  // renames, custom columns) persist across re-running the same query or
  // running a new query that shares columns with the prior one.
  //
  // Custom columns may reference other custom columns, so pruning iterates:
  // dropping a CC may render a dependent CC invalid, requiring another pass.
  useEffect(() => {
    if (!data || data.length === 0) return;
    if (!appliedUrlState.current && initialTableState) return;
    const baseFields = new Set(Object.keys(data[0]));

    setCustomColumns((prev) => {
      let current = prev;
      while (true) {
        const validNames = new Set<string>(baseFields);
        current.forEach((c) => validNames.add(c.field));
        const next = current.filter((cc) => {
          const refs = new Set<string>();
          collectColumnRefs(cc.expr, refs);
          return Array.from(refs).every((r) => validNames.has(r));
        });
        if (next.length === current.length)
          return next.length === prev.length ? prev : next;
        current = next;
      }
    });
    setDisplayNames((prev) => {
      const next: Record<string, string> = {};
      let changed = false;
      for (const [k, v] of Object.entries(prev)) {
        if (baseFields.has(k)) next[k] = v;
        else changed = true;
      }
      return changed ? next : prev;
    });

    // For order and widths, the surviving field set includes base columns,
    // the always-present grid columns (Rank, actions), and current custom
    // column fields. Stale entries pointing at since-deleted CCs are cleaned
    // up here on the next data change after the CC was removed.
    const validFields = new Set<string>(baseFields);
    validFields.add("Rank");
    validFields.add("actions");
    customColumns.forEach((cc) => validFields.add(cc.field));

    setOrderedFields((prev) => {
      if (prev.length === 0) return prev;
      const filtered = prev.filter((f) => validFields.has(f));
      // Dimensions are the row identifier; when a new dimension appears
      // (e.g., user changed dim from Season to TeamDefense), inserting it at
      // the front matches user intent. Without this, the new dim would fall
      // through to columnMap.forEach in the columns memo and land at the very
      // end — past every metric and custom column — making it look "missing."
      const filteredSet = new Set(filtered);
      const newDims = dimensionColumns.filter((d) => !filteredSet.has(d));
      if (newDims.length === 0) {
        return filtered.length === prev.length ? prev : filtered;
      }
      const rankIdx = filtered.indexOf("Rank");
      const insertAt = rankIdx >= 0 ? rankIdx + 1 : 0;
      return [
        ...filtered.slice(0, insertAt),
        ...newDims,
        ...filtered.slice(insertAt),
      ];
    });
    setColumnWidths((prev) => {
      const keys = Object.keys(prev);
      if (keys.length === 0) return prev;
      const next: Record<string, number> = {};
      let changed = false;
      for (const k of keys) {
        if (validFields.has(k)) next[k] = prev[k];
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [data, initialTableState, customColumns, dimensionColumns]);

  // Hide denormalized cross-row columns by default (they exist for filtering, not display)
  useEffect(() => {
    if (hiddenColumns.length === 0) return;
    setColumnVisibilityModel((prev) => {
      const updated = { ...prev };
      for (const col of hiddenColumns) {
        // Only hide if user hasn't explicitly set visibility for this column
        if (!(col in updated)) {
          updated[col] = false;
        }
      }
      return updated;
    });
  }, [hiddenColumns]);

  // Sync default pinned columns and column visibility when isMobile changes
  useEffect(() => {
    setPinnedColumns(
      isMobile
        ? { left: ["Rank"] }
        : embedded
          ? { left: ["Rank"] }
          : { left: ["Rank"], right: ["actions"] },
    );
    setColumnVisibilityModel(isMobile ? { Rank: false } : {});
  }, [isMobile, embedded]);

  // Read URL table state on mount, in two phases.
  //
  // Phase 1 (this effect): apply customColumns ONLY. We let React commit that
  // state and let the columns memo rebuild before applying anything that
  // references a custom column. Without this split, MUI may silently drop a
  // sortModel directive whose field doesn't exist in the columns prop yet —
  // making "share link with sort on a custom column" unreliable.
  //
  // Phase 2 (lives after the `columns` memo, since it reads column types for
  // filter-operator validation): applies sort, visibility, pinning, order, etc.
  // Depends on `customColumns` so it fires AFTER phase 1 has committed.
  useEffect(() => {
    if (!initialTableState || appliedUrlState.current) return;
    if (customColumnsPhaseApplied.current) return;
    if (!enrichedDataBase || enrichedDataBase.length === 0) return;

    const decoded = decodeTableState(initialTableState);
    if (!decoded) return;

    customColumnsPhaseApplied.current = true;

    if (decoded.cc && decoded.cc.length > 0) {
      const baseFields = new Set(Object.keys(enrichedDataBase[0] ?? {}));
      let restored: CustomColumn[] = [];
      for (const sc of decoded.cc) {
        const cc = deserializeCustomColumn(sc);
        if (cc) restored.push(cc);
      }
      // Iteratively prune CCs whose refs aren't satisfied (handles CC→CC chains)
      while (true) {
        const validNames = new Set<string>(baseFields);
        restored.forEach((c) => validNames.add(c.field));
        const next = restored.filter((cc) => {
          const refs = new Set<string>();
          collectColumnRefs(cc.expr, refs);
          return Array.from(refs).every((r) => validNames.has(r));
        });
        if (next.length === restored.length) break;
        restored = next;
      }
      phase1CommittedCcCount.current = restored.length;
      if (restored.length > 0) setCustomColumns(restored);
    } else {
      phase1CommittedCcCount.current = 0;
    }
  }, [enrichedDataBase, initialTableState]);

  // Write table state to URL on every change
  useEffect(() => {
    if (!data || data.length === 0) return;

    const snapshot: TableStateSnapshot = {};

    // Sort — omit if matches default (Poss desc)
    if (
      sortModel.length > 0 &&
      !(
        sortModel.length === 1 &&
        sortModel[0].field === "Poss" &&
        sortModel[0].sort === "desc"
      )
    ) {
      snapshot.s = sortModel.map((s) => ({
        field: s.field,
        sort: s.sort as "asc" | "desc",
      }));
    }

    // Column visibility — omit if matches default
    const defaultVis = isMobile ? { Rank: false } : {};
    if (JSON.stringify(columnVisibilityModel) !== JSON.stringify(defaultVis)) {
      snapshot.v = columnVisibilityModel;
    }

    // Pinned columns — omit if matches default
    const defaultPc = isMobile
      ? { left: ["Rank"] }
      : embedded
        ? { left: ["Rank"] }
        : { left: ["Rank"], right: ["actions"] };
    if (JSON.stringify(pinnedColumns) !== JSON.stringify(defaultPc)) {
      snapshot.pc = pinnedColumns;
    }

    // Pinned rows — omit if empty
    if (
      pinnedRowMatchers.top.length > 0 ||
      pinnedRowMatchers.bottom.length > 0
    ) {
      snapshot.pr = {
        top: pinnedRowMatchers.top,
        bottom: pinnedRowMatchers.bottom,
      };
    }

    // Quick filter
    if (quickFilterValues) {
      snapshot.q = quickFilterValues;
    }

    // Rank mode — omit if default (percentiles)
    if (rankMode !== "percentiles") {
      snapshot.r = rankMode;
    }

    // Filters — omit if empty
    if (filterModel.items && filterModel.items.length > 0) {
      snapshot.f = filterModel.items.map((item: any) => {
        const entry: any = { field: item.field, operator: item.operator };
        if (item.value !== undefined) entry.value = item.value;
        return entry;
      });
    }

    // Custom columns
    if (customColumns.length > 0) {
      snapshot.cc = customColumns.map(serializeCustomColumn);
      snapshot.ver = 1;
    }

    // Column order
    if (orderedFields.length > 0) {
      snapshot.o = orderedFields;
    }

    // Column widths
    if (Object.keys(columnWidths).length > 0) {
      snapshot.cw = columnWidths;
    }

    // Header renames
    if (Object.keys(displayNames).length > 0) {
      snapshot.rn = displayNames;
    }

    if (isDefaultState(snapshot, isMobile)) {
      const url = new URL(window.location.href);
      if (url.searchParams.has("ts")) {
        url.searchParams.delete("ts");
        window.history.replaceState(null, "", url.toString());
      }
    } else {
      window.history.replaceState(null, "", buildUrlWithTableState(snapshot));
    }
  }, [
    // `embedded` changes the default pinned-columns baseline this effect
    // compares against, so a change to it has to recompute the snapshot.
    embedded,
    data,
    sortModel,
    columnVisibilityModel,
    pinnedColumns,
    pinnedRowMatchers,
    quickFilterValues,
    rankMode,
    filterModel,
    customColumns,
    orderedFields,
    columnWidths,
    displayNames,
    isMobile,
  ]);

  const { rows, pinnedRows } = useMemo(() => {
    const rowsData: GridRowModel[] = [];
    const pinnedRowsData: { top: GridRowModel[]; bottom: GridRowModel[] } = {
      top: [],
      bottom: [],
    };
    const topSet = new Set(resolvedPinnedIds.top);
    const bottomSet = new Set(resolvedPinnedIds.bottom);
    const rowsWithIds = enrichedData.map((row, i) =>
      Object.assign({ id: i }, row),
    );
    // Separate pinned rows — preserve matcher order for pinned sections
    for (const id of resolvedPinnedIds.top) {
      pinnedRowsData.top.push(rowsWithIds[id]);
    }
    for (const id of resolvedPinnedIds.bottom) {
      pinnedRowsData.bottom.push(rowsWithIds[id]);
    }
    rowsWithIds.forEach((row) => {
      if (!topSet.has(row.id as number) && !bottomSet.has(row.id as number)) {
        rowsData.push(row);
      }
    });
    return { rows: rowsData, pinnedRows: pinnedRowsData };
  }, [enrichedData, resolvedPinnedIds]);

  // Create complete population array including pinned rows for percentile calculations
  const allRows = useMemo(() => {
    return [...rows, ...pinnedRows.top, ...pinnedRows.bottom];
  }, [rows, pinnedRows.top, pinnedRows.bottom]);

  // Column headers are where someone reads a number they cannot name.
  const { byName: glossaryByName } = useGlossary();

  // Split camelCase/PascalCase into spaced words with natural casing
  // Used for headerName (column menus, filters, visibility panel)
  // e.g. "RimRate" -> "Rim Rate", "TeamOffense" -> "Team Offense", "eFG" -> "eFG", "PPP" -> "PPP"
  const formatHeaderName = (field: string): string => {
    // Don't split fully uppercase abbreviations with optional lowercase prefix
    if (/^[a-z]?[A-Z%0-9]+$/.test(field)) return field;
    // Handle lowercase prefix: keep prefix, space the rest
    if (/^[a-z][A-Z]/.test(field)) {
      const prefix = field[0];
      const rest = field.slice(1);
      const formatted = rest
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
      return formatted.includes(" ")
        ? prefix + "-" + formatted
        : prefix + formatted;
    }
    return field
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
  };

  // Uppercase display variant for column header rendering
  // Preserves lowercase prefixes: "aPPP" stays "aPPP", "iTurnoverUsage" -> "i-TURNOVER USAGE"
  const formatHeaderDisplay = (field: string): string => {
    if (/^[a-z]?[A-Z%0-9]+$/.test(field)) return field;
    if (/^[a-z][A-Z]/.test(field)) {
      const prefix = field[0];
      const rest = field.slice(1);
      const formatted = rest
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
      const upper = formatted.toUpperCase();
      return upper.includes(" ") ? prefix + "-" + upper : prefix + upper;
    }
    const spaced = field
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
    return spaced.toUpperCase();
  };

  const customColumnByField = useMemo(() => {
    const map = new Map<string, CustomColumn>();
    for (const cc of customColumns) map.set(cc.field, cc);
    return map;
  }, [customColumns]);

  // Resolve the header text for a field. Order of precedence:
  //   1. user-set rename (displayNames[field])
  //   2. custom column's own headerName
  //   3. derived from field via formatHeaderName/formatHeaderDisplay
  const resolveHeaderName = (field: string): string => {
    const renamed = displayNames[field];
    if (renamed) return renamed;
    const cc = customColumnByField.get(field);
    if (cc?.headerName) return cc.headerName;
    return formatHeaderName(field);
  };
  // Same precedence chain but skipping the rename — used by the rename UX to
  // show the user what the column would revert to if they cleared the rename.
  const getDefaultHeaderName = (field: string): string => {
    const cc = customColumnByField.get(field);
    if (cc?.headerName) return cc.headerName;
    return formatHeaderName(field);
  };

  // Commit a header rename. Empty input (or input matching the default)
  // clears the override so the column reverts to its derived name.
  const commitRename = (field: string, newName: string) => {
    const trimmed = newName.trim();
    const defaultName = getDefaultHeaderName(field);
    setDisplayNames((prev) => {
      const next = { ...prev };
      if (!trimmed || trimmed === defaultName) {
        delete next[field];
      } else {
        next[field] = trimmed;
      }
      return next;
    });
    setRenamingField(null);
    setRenamingAnchor(null);
  };
  const cancelRename = () => {
    setRenamingField(null);
    setRenamingAnchor(null);
  };
  const resolveHeaderDisplay = (field: string): string => {
    const renamed = displayNames[field];
    if (renamed) return renamed.toUpperCase();
    const cc = customColumnByField.get(field);
    if (cc?.headerName) return cc.headerName.toUpperCase();
    return formatHeaderDisplay(field);
  };

  /**
   * Find the glossary entry behind a column field.
   *
   * Result columns are not bare metric names: a named MetricGroup prefixes them
   * (`HC_PPP`) and a group dimension pivots them (`PPP-PR`), so the raw name has
   * to be recovered before lookup. Renamed and custom columns are skipped —
   * whatever they compute is the author's, not a registry metric.
   */
  const glossaryForField = (field: string) => {
    if (displayNames[field] || customColumnByField.has(field)) return undefined;
    const candidates = [field];
    // A group name may itself hold underscores (keep_hc_PPP): try every split point.
    for (let u = field.indexOf("_"); u > 0; u = field.indexOf("_", u + 1)) {
      candidates.push(field.slice(u + 1));
    }
    candidates.slice().forEach((c) => {
      const dash = c.indexOf("-");
      if (dash > 0) candidates.push(c.slice(0, dash));
    });
    for (const c of candidates) {
      const entry = glossaryByName.get(c);
      if (entry) return entry;
    }
    return undefined;
  };

  const headerTooltip = (field: string) => {
    const entry = glossaryForField(field);
    if (!entry) return "";
    const lines: string[] = [entry.name];
    if (entry.kind === "metric") {
      if (entry.formula) lines.push(entry.formula);
      if (entry.format) lines.push(entry.format);
    } else {
      if (entry.description) lines.push(entry.description);
      lines.push(`${grainLabel(entry.grain)} level`);
    }
    if (entry.statusReason) lines.push(entry.statusReason);
    return lines.join("\n");
  };

  const columns: GridColDef[] = useMemo(() => {
    const baseColDef: GridColDef = {
      field: "Rank",
      headerAlign: "center",
      headerClassName: "data-grid-header",
      align: "center",
      cellClassName: "data-grid-cell",
      width: 120,
      renderHeader: (params) => (
        <Tooltip
          title={
            <span style={{ whiteSpace: "pre-line" }}>
              {headerTooltip(params.field)}
            </span>
          }
          arrow
          placement="top"
          // An empty title renders nothing, so columns with no registry entry
          // (custom or renamed) simply have no tooltip.
          disableHoverListener={!headerTooltip(params.field)}
        >
          <span className="MuiDataGrid-columnHeaderTitle">
            {resolveHeaderDisplay(params.field)}
          </span>
        </Tooltip>
      ),
    };

    const minColDef: GridColTypeDef = {
      sortable: false,
      filterable: false,
      groupable: false,
      disableColumnMenu: true,
      resizable: false,
      width: 90,
    };

    // Known column types that should override automatic type inference
    const knownColumnTypes: Record<string, ColumnType> = {
      OffensiveChancePlayer: "string",
      DefensiveChancePlayer: "string",
      TeamOffense: "string",
      TeamDefense: "string",
      Poss: "number", // Explicitly set Poss as numeric
      // Add more known column types here
    };

    // Function to determine if a value is numeric
    const isNumericValue = (value: any): boolean => {
      if (value === null || value === undefined || value === "") return false;
      if (value === "NaN" || value === "inf") return false;
      if (typeof value === "boolean") return false;
      if (typeof value === "string" && value.endsWith("%")) {
        return !isNaN(parseFloat(value));
      }
      return !isNaN(+value);
    };

    // Function to determine if a value is a date string
    const isDateValue = (value: any): boolean => {
      if (value === null || value === undefined || value === "") return false;
      if (typeof value !== "string") return false;
      // Check for common date patterns:
      // Compact format: 20260115 (yyyymmdd) - this is what the API returns
      // ISO format: 2026-01-15
      // US format: 1/15/2026 or 01/15/2026
      // Also handles datetime: 2026-01-15T00:00:00
      const datePatterns = [
        /^\d{8}$/, // Compact date format: yyyymmdd
        /^\d{4}-\d{2}-\d{2}/, // ISO date (with optional time)
        /^\d{1,2}\/\d{1,2}\/\d{4}$/, // US date format
      ];
      if (!datePatterns.some((pattern) => pattern.test(value))) return false;
      // Verify it parses to a valid date
      // For compact format (yyyymmdd), need to parse explicitly
      const parsed = /^\d{8}$/.test(value)
        ? dayjs(value, "YYYYMMDD")
        : dayjs(value);
      return parsed.isValid() && parsed.year() > 1900 && parsed.year() < 2100;
    };

    // Function to infer column type with better accuracy
    const inferColumnType = (header: string, columnData: any[]): ColumnType => {
      // First check if we have a known type for this column
      if (knownColumnTypes[header]) {
        return knownColumnTypes[header];
      }

      // Check if column name matches a known filter-context metric
      // (Video, ShotChart, iShotChart, ActionChart, iActionChart — and legacy *Detail* variants).
      // These return {filters:[...]} JSON instead of full data payloads.
      if (getFilterContextMetricType(header)) {
        return "filterContext";
      }

      // Scan the column for up to TARGET_SAMPLE non-empty values. Sparse columns
      // (lots of "nan"/"--") can have all-empty leading rows, so a fixed-position
      // slice misses the actual data.
      const TARGET_SAMPLE = 10;
      let numericCount = 0;
      let booleanCount = 0;
      let dateCount = 0;
      let nonEmptyCount = 0;

      for (let i = 0; i < columnData.length; i++) {
        const value = columnData[i];
        if (
          value === null ||
          value === undefined ||
          value === "" ||
          value === "NaN" ||
          value === "nan" ||
          value === "inf" ||
          value === "--"
        ) {
          continue;
        }
        nonEmptyCount++;
        if (
          typeof value === "boolean" ||
          value === "True" ||
          value === "False"
        ) {
          booleanCount++;
        } else if (isDateValue(value)) {
          dateCount++;
        } else if (isNumericValue(value)) {
          numericCount++;
        }
        if (nonEmptyCount >= TARGET_SAMPLE) break;
      }

      // If all values are empty, default to string
      if (nonEmptyCount === 0) return "string";

      // Require at least 80% of non-empty values to match a type
      const threshold = nonEmptyCount * 0.8;

      if (booleanCount >= threshold) return "boolean";
      if (dateCount >= threshold) return "date";
      if (numericCount >= threshold) return "number";
      return "string";
    };

    if (!enrichedData || enrichedData.length === 0) return [];

    // Compute content-aware column width based on actual cell values
    const computeColumnWidth = (header: string, colType: string): number => {
      // Sample up to 50 rows to find max content length
      const sampleSize = Math.min(50, enrichedData.length);
      let maxLen = 0;
      for (let r = 0; r < sampleSize; r++) {
        const val = enrichedData[r][header];
        if (val != null && typeof val !== "object") {
          const len = String(val).length;
          if (len > maxLen) maxLen = len;
        }
      }
      // On desktop, also consider header length so columns aren't narrower than their header
      // On mobile, only use cell content length to keep columns as tight as possible
      const effectiveLen = isMobile
        ? maxLen
        : Math.max(maxLen, formatHeaderDisplay(header).length);
      const minLen = isMobile ? 100 : 120;
      // ~10px per character + 60px padding for sort icon / cell padding
      return Math.max(minLen, Math.min(220, effectiveLen * 10 + 60));
    };

    const rankColumn: GridColDef = {
      ...baseColDef,
      ...minColDef,
      headerName: "Rank",
      type: "number",
      width: 60,
      valueGetter: (value, row) => {
        const sortedRowIds = gridExpandedSortedRowIdsSelector(apiRef);
        const index = sortedRowIds.indexOf(row.id);
        return index === -1 ? "" : index + 1;
      },
    };
    const actionColumn: GridColDef = {
      ...minColDef,
      field: "actions",
      type: "actions",
      width: 90,
      getActions: (params) => {
        const rowIdx = params.id as number;
        const isPinnedTop = resolvedPinnedIds.top.includes(rowIdx);
        const isPinnedBottom = resolvedPinnedIds.bottom.includes(rowIdx);
        if (isPinnedTop || isPinnedBottom) {
          return [
            <GridActionsCellItem
              label="Unpin"
              icon={
                <Tooltip title="Unpin">
                  {isPinnedTop ? <ArrowDownward /> : <ArrowUpward />}
                </Tooltip>
              }
              onClick={() => {
                // Find and remove the first matcher that matches this row
                const row = enrichedData[rowIdx];
                const position = isPinnedTop ? "top" : "bottom";
                setPinnedRowMatchers((prev) => {
                  const idx = prev[position].findIndex((m) =>
                    rowMatchesPinMatcher(m, row),
                  );
                  if (idx === -1) return prev;
                  return {
                    ...prev,
                    [position]: prev[position].filter((_, i) => i !== idx),
                  };
                });
              }}
            />,
          ];
        }
        return [
          <GridActionsCellItem
            icon={
              <Tooltip title="Pin to top">
                <ArrowUpward />
              </Tooltip>
            }
            label="Pin to top"
            onClick={() => {
              const matcher = buildPinMatcher(
                enrichedData[rowIdx],
                dimensionColumns,
              );
              setPinnedRowMatchers((prev) => ({
                ...prev,
                top: [...prev.top, matcher],
              }));
            }}
          />,
          <GridActionsCellItem
            icon={
              <Tooltip title="Pin to bottom">
                <ArrowDownward />
              </Tooltip>
            }
            label="Pin to bottom"
            onClick={() => {
              const matcher = buildPinMatcher(
                enrichedData[rowIdx],
                dimensionColumns,
              );
              setPinnedRowMatchers((prev) => ({
                ...prev,
                bottom: [...prev.bottom, matcher],
              }));
            }}
          />,
        ];
      },
    };

    // Build a map of column types for use in filtering logic
    type ColumnType =
      | "string"
      | "number"
      | "boolean"
      | "filterContext"
      | "date";
    const columnTypeMap: Record<string, ColumnType> = {};
    Object.keys(enrichedData[0]).forEach((header) => {
      const columnValues = enrichedData.map((row) => row[header]);
      columnTypeMap[header] = inferColumnType(header, columnValues);
    });

    // Helper to parse our yyyymmdd date strings to timestamps for comparison
    const parseDateValue = (value: any): number | null => {
      if (!value) return null;
      // Handle Date objects (from MUI filter)
      if (value instanceof Date) {
        return value.getTime();
      }
      // Handle our yyyymmdd string format
      if (typeof value === "string" && /^\d{8}$/.test(value)) {
        const parsed = dayjs(value, "YYYYMMDD");
        return parsed.isValid() ? parsed.valueOf() : null;
      }
      // Handle ISO format or other date strings
      if (typeof value === "string") {
        const parsed = dayjs(value);
        return parsed.isValid() ? parsed.valueOf() : null;
      }
      return null;
    };

    const columnDef: GridColDef[] = Object.keys(enrichedData[0]).map(
      (header) => {
        // Get column type from pre-built map
        const colType = columnTypeMap[header];
        const isnum = colType === "number";

        if (colType === "filterContext") {
          const metricType = getFilterContextMetricType(header)!;
          return {
            ...baseColDef,
            ...minColDef,
            resizable: true,
            field: header,
            type: "string",
            renderCell: (params) => {
              const coldata = params.value as string;
              return (
                <FilterContextCell
                  data={coldata}
                  columnName={header}
                  metricType={metricType}
                  queryContext={queryContext}
                  environment={environment}
                  valueLookup={valueLookup}
                />
              );
            },
          };
        } else if (colType === "date") {
          // Date columns need special handling:
          // - valueGetter converts string to Date for MUI filtering
          // - renderCell formats the display
          return {
            ...baseColDef,
            field: header,
            headerName: resolveHeaderName(header),
            width: 140,
            type: "date",
            valueGetter: (value: any) => {
              if (!value) return null;
              // Convert our yyyymmdd string to Date object for MUI filtering
              if (typeof value === "string" && /^\d{8}$/.test(value)) {
                return dayjs(value, "YYYYMMDD").toDate();
              }
              // Handle other date formats
              const parsed = dayjs(value);
              return parsed.isValid() ? parsed.toDate() : null;
            },
            renderCell: (params) => {
              // params.value is now a Date object from valueGetter
              const dateValue = params.value as Date | null;
              if (!dateValue) return <span>––</span>;
              return <span>{dayjs(dateValue).format("MMM DD, YYYY")}</span>;
            },
          };
        } else {
          const customCol = customColumnByField.get(header);
          const formatRawForDisplay = (raw: string): string => {
            if (customCol?.format)
              return formatCustomValue(raw, customCol.format);
            return raw === "nan" ? "––" : raw;
          };
          return {
            ...baseColDef,
            field: header,
            headerName: resolveHeaderName(header),
            width: computeColumnWidth(header, colType),
            type: colType,
            sortComparator: !isnum
              ? gridStringOrNumberComparator
              : (v1, v2) => {
                  if (v1 === "inf" && v2 === "inf") return 0;
                  if (v1 === "inf") return 1;
                  if (v2 === "inf") return -1;
                  const n1 = parseFloat(v1);
                  const n2 = parseFloat(v2);
                  if (isNaN(n1) && isNaN(n2)) return 0;
                  if (isNaN(n1)) return -1;
                  if (isNaN(n2)) return 1;
                  return n1 - n2;
                },
            renderCell: (params) => {
              const coldata = params.value as string;
              var displayValue = formatRawForDisplay(coldata);

              if (
                rankMode === "off" ||
                !isnum ||
                coldata === "nan" ||
                coldata === "inf"
              ) {
                return <span>{displayValue?.toString() ?? "--"}</span>;
              }

              // Filter and validate column values for percentiles / ranks
              // We need to manually apply only column filters (filterModel.items), excluding quick filter
              // This ensures percentiles are calculated from column-filtered population only

              // Step 1: Apply column filters manually (from filterModel.items only, excluding quickFilterValues)
              // IMPORTANT: Pinned rows should always be included in percentile calculations, regardless of filters
              let columnFilteredRows = allRows;
              if (filterModel.items && filterModel.items.length > 0) {
                // Separate pinned rows from non-pinned rows
                const pinnedRowIds = new Set([
                  ...resolvedPinnedIds.top,
                  ...resolvedPinnedIds.bottom,
                ]);
                const pinnedRowsList = allRows.filter((rowData) =>
                  pinnedRowIds.has(rowData.id),
                );
                const nonPinnedRows = allRows.filter(
                  (rowData) => !pinnedRowIds.has(rowData.id),
                );

                // Filter only non-pinned rows based on column filters
                const filteredNonPinnedRows = nonPinnedRows.filter(
                  (rowData) => {
                    // Check if row passes all column filters
                    return filterModel.items.every((filterItem: any) => {
                      const rawFieldValue = rowData[filterItem.field];
                      const fieldColType = columnTypeMap[filterItem.field];

                      // Handle date column filtering
                      if (fieldColType === "date") {
                        const fieldTimestamp = parseDateValue(rawFieldValue);
                        const filterTimestamp = parseDateValue(
                          filterItem.value,
                        );

                        if (fieldTimestamp === null) return false;
                        if (filterTimestamp === null) return true;

                        switch (filterItem.operator) {
                          case "is":
                            // Compare dates at day level (ignore time)
                            return dayjs(fieldTimestamp).isSame(
                              dayjs(filterTimestamp),
                              "day",
                            );
                          case "not":
                            return !dayjs(fieldTimestamp).isSame(
                              dayjs(filterTimestamp),
                              "day",
                            );
                          case "after":
                            return fieldTimestamp > filterTimestamp;
                          case "onOrAfter":
                          case ">=":
                            return fieldTimestamp >= filterTimestamp;
                          case "before":
                            return fieldTimestamp < filterTimestamp;
                          case "onOrBefore":
                          case "<=":
                            return fieldTimestamp <= filterTimestamp;
                          case "isEmpty":
                            return !rawFieldValue;
                          case "isNotEmpty":
                            return !!rawFieldValue;
                          default:
                            return true;
                        }
                      }

                      // Handle non-date filtering (existing logic)
                      const fieldValue = String(
                        rawFieldValue || "",
                      ).toLowerCase();
                      const filterValue = String(
                        filterItem.value || "",
                      ).toLowerCase();

                      switch (filterItem.operator) {
                        case "contains":
                          return fieldValue.includes(filterValue);
                        case "doesNotContain":
                          return !fieldValue.includes(filterValue);
                        case "equals":
                        case "=":
                          return fieldValue === filterValue;
                        case "doesNotEqual":
                        case "!=":
                        case "not":
                          return fieldValue !== filterValue;
                        case "startsWith":
                          return fieldValue.startsWith(filterValue);
                        case "endsWith":
                          return fieldValue.endsWith(filterValue);
                        case "isEmpty":
                          return fieldValue === "";
                        case "isNotEmpty":
                          return fieldValue !== "";
                        case "is":
                          return fieldValue === filterValue;
                        case "isAnyOf":
                          // filterItem.value should be an array for isAnyOf
                          if (Array.isArray(filterItem.value)) {
                            const filterValues = filterItem.value.map(
                              (v: any) => String(v || "").toLowerCase(),
                            );
                            return filterValues.includes(fieldValue);
                          }
                          return fieldValue === filterValue;
                        case ">":
                          return (
                            parseFloat(fieldValue) > parseFloat(filterValue)
                          );
                        case ">=":
                          return (
                            parseFloat(fieldValue) >= parseFloat(filterValue)
                          );
                        case "<":
                          return (
                            parseFloat(fieldValue) < parseFloat(filterValue)
                          );
                        case "<=":
                          return (
                            parseFloat(fieldValue) <= parseFloat(filterValue)
                          );
                        default:
                          return true; // Unknown operator, include row
                      }
                    });
                  },
                );

                // Combine filtered non-pinned rows with all pinned rows
                columnFilteredRows = [
                  ...filteredNonPinnedRows,
                  ...pinnedRowsList,
                ];
              }

              // Use column-filtered rows (excluding quick filter) for percentile calculations
              // Note: We don't need sorted order for percentile calculation, just the population
              const filteredRows = columnFilteredRows;

              const columnValues = filteredRows.map((rowData) =>
                parseFloat(rowData[header]),
              );
              const validValues = columnValues.filter((value) => !isNaN(value));

              // Calculate percentile
              const sortedValues = validValues.sort((a, b) => a - b);
              const valueIndex = sortedValues.indexOf(parseFloat(coldata));
              const percentile =
                valueIndex === 0
                  ? 0
                  : valueIndex === sortedValues.length - 1
                    ? 100
                    : Math.round(
                        ((valueIndex + 1) / sortedValues.length) * 100,
                      );

              if (rankMode === "percentiles") {
                return (
                  <PercentileCell
                    value={displayValue}
                    percentile={percentile}
                    mode={rankMode}
                  />
                );
              }

              if (rankMode === "ranks") {
                // Calculate rank
                const sortedValues = validValues.sort((a, b) => b - a);
                const valueIndex = sortedValues.indexOf(parseFloat(coldata));
                const rank = valueIndex + 1;

                return (
                  <PercentileCell
                    value={displayValue}
                    percentile={percentile}
                    rank={rank}
                    mode={rankMode}
                  />
                );
              }
            },
            valueFormatter: (value, row) =>
              customCol?.format ? formatRawForDisplay(value as string) : value,
          };
        }
      },
    );
    // Embedded mode drops the per-row pin-actions column (we want a
    // chromeless grid). Mobile already drops it for screen-real-estate
    // reasons, so the two flags collapse into the same branch.
    let finalColumns: GridColDef[] =
      isMobile || embedded
        ? [rankColumn].concat(columnDef)
        : [actionColumn, rankColumn].concat(columnDef);

    // Apply saved column order if user has reordered columns
    if (orderedFields.length > 0) {
      const columnMap = new Map(finalColumns.map((col) => [col.field, col]));
      const orderedColumns: GridColDef[] = [];

      // First, add columns in the saved order
      orderedFields.forEach((field) => {
        const col = columnMap.get(field);
        if (col) {
          orderedColumns.push(col);
          columnMap.delete(field);
        }
      });

      // Place leftover columns: new dimension fields go to the front (after
      // Rank), everything else to the end. Without the front-insert, a swap
      // like Season → TeamDefense lands the new dim past every metric and
      // custom column — looks "missing" to the user. The pruning effect also
      // updates orderedFields to include new dims, but this branch covers the
      // single render between the data update and that effect firing, plus
      // any path that bypasses the effect.
      const dimSet = new Set(dimensionColumns);
      const leftoverDims: GridColDef[] = [];
      const leftoverOthers: GridColDef[] = [];
      columnMap.forEach((col) => {
        if (dimSet.has(col.field)) leftoverDims.push(col);
        else leftoverOthers.push(col);
      });
      if (leftoverDims.length > 0) {
        const rankIdx = orderedColumns.findIndex((c) => c.field === "Rank");
        orderedColumns.splice(
          rankIdx >= 0 ? rankIdx + 1 : 0,
          0,
          ...leftoverDims,
        );
      }
      leftoverOthers.forEach((col) => orderedColumns.push(col));

      finalColumns = orderedColumns;
    }

    // Apply saved column widths if user has resized columns
    if (Object.keys(columnWidths).length > 0) {
      finalColumns = finalColumns.map((col) => {
        const savedWidth = columnWidths[col.field];
        if (savedWidth !== undefined) {
          return { ...col, width: savedWidth };
        }
        return col;
      });
    }

    return finalColumns;
    // sortModel is needed to trigger rank column recalculation when sort changes,
    // even though we don't use it directly (we use gridExpandedSortedRowIdsSelector)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enrichedData,
    apiRef,
    resolvedPinnedIds,
    dimensionColumns,
    rankMode,
    filterModel,
    allRows,
    sortModel,
    orderedFields,
    columnWidths,
    isMobile,
    queryContext,
    environment,
    valueLookup,
    customColumnByField,
    displayNames,
  ]);

  // Phase 2 of saved-state restore. Placed after `columns` so column types are
  // in scope for filter-operator validation. Gated on Phase 1 having committed
  // its customColumns; `appliedUrlState` guards against re-application.
  useEffect(() => {
    if (!initialTableState || appliedUrlState.current) return;
    if (!customColumnsPhaseApplied.current) return;
    if (!enrichedDataBase || enrichedDataBase.length === 0) return;
    if (
      phase1CommittedCcCount.current >= 0 &&
      customColumns.length !== phase1CommittedCcCount.current
    ) {
      return;
    }

    const decoded = decodeTableState(initialTableState);
    if (!decoded) return;

    appliedUrlState.current = true;

    const validColumns = new Set(Object.keys(enrichedDataBase[0] ?? {}));
    validColumns.add("Rank");
    validColumns.add("actions");
    customColumns.forEach((cc) => validColumns.add(cc.field));

    if (decoded.s) {
      const validSort = decoded.s.filter((s) => validColumns.has(s.field));
      if (validSort.length > 0) setSortModel(validSort as GridSortModel);
    }
    if (decoded.v) {
      const validVis: Record<string, boolean> = {};
      for (const [key, val] of Object.entries(decoded.v)) {
        if (validColumns.has(key) || key === "Rank") validVis[key] = val;
      }
      setColumnVisibilityModel(validVis);
    }
    if (decoded.pc) {
      const filterValid = (arr?: string[]) =>
        arr?.filter((c) => validColumns.has(c)) ?? [];
      setPinnedColumns({
        left: filterValid(decoded.pc.left),
        right: filterValid(decoded.pc.right),
      });
    }
    if (decoded.pr) {
      const filterValid = (matchers?: PinMatcher[]) =>
        (matchers ?? []).filter(
          (m) =>
            Object.keys(m).length > 0 &&
            Object.keys(m).every((k) => validColumns.has(k)),
        );
      setPinnedRowMatchers({
        top: filterValid(decoded.pr.top),
        bottom: filterValid(decoded.pr.bottom),
      });
    }
    if (decoded.q) setQuickFilterValues(decoded.q);
    if (decoded.r) setRankMode(decoded.r);
    if (decoded.f) {
      const colTypeByField: Record<string, string> = {};
      columns.forEach((c) => {
        colTypeByField[c.field] = (c.type as string) ?? "string";
      });
      const validFilters = decoded.f.filter((f) => {
        if (!validColumns.has(f.field)) return false;
        const colType = colTypeByField[f.field] ?? "string";
        const validOps =
          VALID_OPERATORS_BY_TYPE[colType] ?? VALID_OPERATORS_BY_TYPE.string;
        return validOps.has(f.operator);
      });
      setFilterModel({ items: validFilters as any });
    }
    if (decoded.o) {
      const validOrder = decoded.o.filter((c) => validColumns.has(c));
      if (validOrder.length > 0) setOrderedFields(validOrder);
    }
    if (decoded.cw) {
      const validWidths: Record<string, number> = {};
      for (const [k, v] of Object.entries(decoded.cw)) {
        if (validColumns.has(k) && typeof v === "number" && v > 0) {
          validWidths[k] = v;
        }
      }
      if (Object.keys(validWidths).length > 0) setColumnWidths(validWidths);
    }
    if (decoded.rn) {
      const validRenames: Record<string, string> = {};
      for (const [k, v] of Object.entries(decoded.rn)) {
        if (validColumns.has(k) && typeof v === "string" && v.length > 0) {
          validRenames[k] = v;
        }
      }
      if (Object.keys(validRenames).length > 0) setDisplayNames(validRenames);
    }
  }, [enrichedDataBase, customColumns, initialTableState, columns]);

  return (
    <div
      className={
        embedded ? "data-grid-container data-grid-embedded" : "data-grid-container"
      }
    >
      <DataGridPremium
        loading={loading}
        rows={rows}
        columns={columns}
        pinnedColumns={pinnedColumns}
        onPinnedColumnsChange={(newPinnedColumns) => {
          setPinnedColumns(newPinnedColumns);
          // Blur to prevent focus-scroll jump after pin action
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
        }}
        pinnedRows={pinnedRows}
        headerFilterHeight={40}
        rowHeight={36}
        disableColumnMenu={embedded}
        getRowClassName={(params) =>
          params.indexRelativeToCurrentPage % 2 === 0
            ? "data-grid-row even"
            : "data-grid-row odd"
        }
        slots={embedded ? EMBEDDED_SLOTS : DEFAULT_SLOTS}
        slotProps={{
          toolbar: {
            csvOptions: {
              getRowsToExport: () => gridExpandedSortedRowIdsSelector(apiRef),
            },
            excelOptions: {
              getRowsToExport: () => gridExpandedSortedRowIdsSelector(apiRef),
            },
            printOptions: { disableToolbarButton: true },
            showQuickFilter: true,
            percentileProps: percentileProps,
            isMobile: isMobile,
          },
          columnsPanel: {
            customColumns,
            setCustomColumns,
            displayNames,
            setDisplayNames,
            columnVisibilityModel,
            setColumnVisibilityModel,
            baseFields: enrichedDataBase[0]
              ? Object.keys(enrichedDataBase[0])
              : [],
            allFields: enrichedData[0] ? Object.keys(enrichedData[0]) : [],
            sampleRows: enrichedData.slice(0, 3),
            apiRef,
            isMobile,
            resolveHeaderName,
            getDefaultHeaderName,
            dimensionColumns,
            orderedFields,
            setOrderedFields,
            openCustomColumnEditorRef,
          } as any,
          columnMenu: {
            onRequestRename: requestRename,
            onRequestNewCustomColumn: requestNewCustomColumn,
          } as any,
        }}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        density="compact"
        columnHeaderHeight={40}
        disableAggregation
        disableDensitySelector
        hideFooter
        sortingOrder={["desc", "asc"]}
        apiRef={apiRef}
        ignoreDiacritics
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          setSortModel(newSortModel);
          // Blur the focused column header to prevent MUI from auto-scrolling to it
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
        }}
        onColumnOrderChange={() => {
          // Capture the current column order when user reorders columns
          const currentOrder = apiRef.current
            .getAllColumns()
            .map((col) => col.field);
          setOrderedFields(currentOrder);
        }}
        onColumnWidthChange={(params) => {
          // Capture the new column width when user resizes a column
          setColumnWidths((prev) => ({
            ...prev,
            [params.colDef.field]: params.width,
          }));
        }}
        filterModel={filterModel}
        onFilterModelChange={(newFilterModel) => setFilterModel(newFilterModel)}
        onStateChange={(state, event, details) => {
          // Access quick filter values from the filter state
          const filterState = state.filter as any;
          const currentQuickFilter =
            filterState?.filterModel?.quickFilterValues ||
            filterState?.quickFilterValues ||
            "";

          // Convert to string if it's an array
          const quickFilterString = Array.isArray(currentQuickFilter)
            ? currentQuickFilter.join(" ")
            : String(currentQuickFilter || "");

          if (quickFilterString !== quickFilterValues) {
            setQuickFilterValues(quickFilterString);
          }
        }}
        sx={{
          [`& .${gridClasses.columnSeparator}`]: {
            visibility: "visible",
            [`&:not(.${gridClasses["columnSeparator--resizable"]})`]: {
              display: "none",
            },
          },
          ".MuiDataGrid-columnHeader, .MuiDataGrid-filler": {
            backgroundColor: "#f5f5f5",
          },
          ".MuiDataGrid-columnHeaders": {
            borderBottom: "2px solid #4c4c4c",
          },
          ".MuiDataGrid-iconButtonContainer": {
            width: "auto !important",
            order: -1,
          },
          ".MuiDataGrid-sortIcon": {
            color: "#292929",
          },
          ".MuiDataGrid-menuIcon": {
            width: "auto !important",
          },
        }}
      />
      {renamingField && (
        <HeaderRenamePopper
          field={renamingField}
          anchorEl={renamingAnchor}
          initial={resolveHeaderName(renamingField)}
          placeholder={getDefaultHeaderName(renamingField)}
          onSave={(name) => commitRename(renamingField, name)}
          onCancel={cancelRename}
        />
      )}
    </div>
  );
}
